import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Box, Text } from '@commonsku/design-system';
import {
  selectMessagesForConversation,
  selectLoading,
  sendChatMessage,
  selectMessageError,
  selectConversationError,
  setActiveConversation,
  setMessages,
  setInitialContextSent,
  setPreviewRevision,
  setConversationTranscript,
} from '../../redux/presentationBuilderChat';
import { ChatMessages } from '../skubot/ChatMessages';
import { ConnectedPlusInfoBox } from './ConnectedPlusInfoBox';
import {
  PresentationChatStartingState,
  PRESENTATION_CHAT_DISCLAIMER,
  PRESENTATION_CHAT_PLACEHOLDER,
} from './PresentationChatStartingState';
import type { PresentationChatContext } from './buildPresentationChatContext';

export const SKUBOT_PANEL_WIDTH = 400;


const PanelRoot = styled.aside`
  &&& {
    display: flex;
    flex: 1;
    flex-direction: column;
    width: ${SKUBOT_PANEL_WIDTH}px;
    min-width: ${SKUBOT_PANEL_WIDTH}px;
    max-width: ${SKUBOT_PANEL_WIDTH}px;
    height: 100%;
    min-height: 0;
    background: #fafafa;
    border: 1px solid #e4e4e7;
    overflow: hidden;
  }
`;

const PanelHeader = styled.div`
  &&& {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    height: 56px;
    padding: 12px 8px 12px 16px;
    border-bottom: 1px solid #e4e4e7;
    background: #fff;
  }
`;

const PresentationChatMessages = styled(ChatMessages)`
  &&& {
    .skubot-chat-thread {
      max-width: none;
      padding: 16px;
      gap: 16px;
    }

    .skubot-chat-input-section {
      padding-bottom: 16px;
    }

    .skubot-chat-input {
      max-width: none;
      padding: 16px;
    }
  }
`;

const PresentationChatWelcome = styled(PresentationChatStartingState)`
  &&& {
    .skubot-welcome-content {
      max-width: none;
    }

    .skubot-chat-input-section {
      padding-bottom: 16px;
    }

    .skubot-chat-input {
      max-width: none;
    }
  }
`;

interface PresentationSkubotPanelProps {
  disabled?: boolean;
  chatContext: PresentationChatContext;
}

export default function PresentationSkubotPanel({
  disabled = false,
  chatContext,
}: PresentationSkubotPanelProps) {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectLoading);
  const messages = useSelector(selectMessagesForConversation);
  const messagesError = useSelector(selectMessageError);
  const conversationError = useSelector(selectConversationError);
  const [input, setInput] = useState('');
  const hasProducts = chatContext.currentProducts.length > 0;
  const showStartingState = messages.length === 0 && !isLoading && !messagesError;

  useEffect(() => {
    dispatch(setActiveConversation(undefined));
    dispatch(setMessages({}));
    dispatch(setInitialContextSent(false));
    dispatch(setPreviewRevision(0));
    dispatch(setConversationTranscript([]));
  }, [chatContext.orderId, dispatch]);

  const handleSend = useCallback((message?: string) => {
    if (disabled) {
      return;
    }
    const inputMessage = message ?? input;
    if (!inputMessage) {
      return;
    }
    dispatch(sendChatMessage(
      inputMessage,
      chatContext.clientName,
      chatContext.tenantId,
      chatContext.orderId,
      chatContext.eventType,
      chatContext.totalBudget,
      chatContext.currentProducts,
    ));
    setInput('');
  }, [chatContext, disabled, dispatch, input]);

  return (
    <PanelRoot>
      <PanelHeader>
        <Text
          margin="0"
          fontSize="md"
          fontWeight="semibold"
          color="gray.900"
        >
          Skubot
        </Text>
      </PanelHeader>

      <Box display="flex" flex="1" minW={0} minH={0} flexDirection="column">
        {conversationError ? (
          <Text
            margin="16px 16px 0"
            fontSize="sm"
            color="red.500"
          >
            {conversationError}
          </Text>
        ) : null}

        {showStartingState ? (
          <PresentationChatWelcome
            hasProducts={hasProducts}
            input={input}
            onInputChange={setInput}
            onSend={handleSend}
            disabled={disabled}
          />
        ) : (
          <PresentationChatMessages
            messages={messages}
            input={input}
            onInputChange={setInput}
            onSend={handleSend}
            onFeedback={undefined}
            disabled={disabled}
            isLoading={isLoading}
            errorMessage={messagesError}
            threadHeader={<ConnectedPlusInfoBox />}
            placeholder={PRESENTATION_CHAT_PLACEHOLDER}
            disclaimer={PRESENTATION_CHAT_DISCLAIMER}
          />
        )}
      </Box>
    </PanelRoot>
  );
}
